import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Divider from '@zenbusiness/zui/Divider';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { useNavigate } from 'react-router-dom';

import { MODAL_NAMES } from '#client/constants/modals';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useSCorpBankingPrice from '#client/hooks/useSCorpBankingPrice';

import { StepNumber } from './styles';

const steps = [
  {
    title: 'Bank and run your business in one place',
    description: 'Trade all the apps for one simple solution and easily apply in just a few clicks.'
  },
  {
    title: 'Lower fees',
    description:
      'No opening deposit, minimum balance, or usage requirements. Plus, lower wire transfer fees than traditional banks.'
  },
  {
    title: '2 types of debit cards',
    description: 'Use your virtual or physical card as needed for more convenience and security.'
  },
  {
    title: 'Many ways to add funds',
    description: 'Easily fund your business from a personal account and accept customer payments.'
  },
  {
    title: 'Premium features',
    description:
      'Free Invoicing and Instant Deposit — get customizable templates and options to get paid online faster.'
  }
];

export const HowToUse = () => {
  const navigate = useNavigate();
  const { bankingPrice } = useSCorpBankingPrice();

  const {
    sizes: { isDesktop, isMobile }
  } = useBreakpoints();

  const trackingButtonPressed = useButtonPressedCallback({
    elementText: `Get ZenBusiness Banking for ${bankingPrice}`,
    elementName: 'buy_zb_banking_benefits_section_catbankupsell',
    appPageName: 'category_selling_banking_landing'
  });

  const handleClick = () => {
    trackingButtonPressed();
    navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`);
  };
  return (
    <Stack spacing={4}>
      <Stack
        alignItems={isMobile ? 'flex-start' : 'center'}
        direction={isMobile ? 'column' : 'row'}
        spacing={1}
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={1}
        >
          <Box maxWidth="140px">
            <SharedAsset
              alt="Zen Logo"
              src="/logo/new-zen-logo.svg"
            />
          </Box>
          <Divider
            height={2}
            orientation="vertical"
          />
        </Stack>
        <Text
          color="primary"
          size={isMobile ? 'md' : 'xl'}
          weight="medium"
        >
          Business Banking Account
        </Text>
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={4}
      >
        <Stack
          alignItems="center"
          spacing={3}
          width={isDesktop ? '50%' : '100%'}
        >
          <Stack spacing={3}>
            {steps.map(({ title, description }, index) => (
              <Stack
                alignItems="center"
                direction="row"
                key={title}
                spacing="20px"
              >
                <StepNumber
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    color="white"
                    size="lg"
                    weight="medium"
                  >
                    {index + 1}
                  </Text>
                </StepNumber>
                <Stack spacing={1}>
                  <Text
                    color="primary"
                    size={isMobile ? 'xs' : 'md'}
                    weight="medium"
                  >
                    {title}
                  </Text>
                  <Text
                    color="primary"
                    size={isMobile ? 'xs' : 'md'}
                  >
                    {description}
                  </Text>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Box
            marginTop={2}
            width="100%"
          >
            <Button
              color="primary"
              fullWidth
              onClick={handleClick}
              variant="contained"
            >
              <Text size={isMobile ? 'xs' : 'md'}>Get ZenBusiness Banking for {bankingPrice}</Text>
            </Button>
          </Box>
        </Stack>
        {isDesktop && (
          <Box width="50%">
            <SharedAsset
              alt="Zen Logo"
              src="/s2/vector/bank-locked-desktop-mobile-product-group.svg"
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
